import { apiClient } from './client.ts';
import type {
  AdminUser,
  AdminStats,
  Tariff,
  PaymentDetails,
  House,
} from '../types/models.ts';

// ─── Users ────────────────────────────────────────────────────────────────────

export async function getAdminUsers(): Promise<AdminUser[]> {
  return apiClient.get('admin/users').json<AdminUser[]>();
}

export async function approveUser(userId: string): Promise<AdminUser> {
  return apiClient.patch(`admin/users/${userId}/approve`).json<AdminUser>();
}

export async function updateAdminUser(
  userId: string,
  input: Partial<Pick<AdminUser, 'fullName' | 'email' | 'phone' | 'role' | 'isApproved'>>
): Promise<AdminUser> {
  return apiClient.patch(`admin/users/${userId}`, { json: input }).json<AdminUser>();
}

// ─── Stats ────────────────────────────────────────────────────────────────────

export async function getAdminStats(period?: string): Promise<AdminStats> {
  const url = period ? `admin/stats?period=${period}` : 'admin/stats';
  return apiClient.get(url).json<AdminStats>();
}

// ─── Tariffs ──────────────────────────────────────────────────────────────────

export async function getAdminTariffs(): Promise<Tariff[]> {
  return apiClient.get('admin/tariffs').json<Tariff[]>();
}

export async function createAdminTariff(input: {
  periodFrom: string;
  periodTo?: string;
  coldWaterRate: number;
  hotWaterRate: number;
  wastewaterRate: number;
  notes?: string;
}): Promise<Tariff> {
  return apiClient.post('admin/tariffs', { json: input }).json<Tariff>();
}

export async function updateAdminTariff(
  tariffId: string,
  input: Partial<{
    periodTo: string | null;
    coldWaterRate: number;
    hotWaterRate: number;
    wastewaterRate: number;
    notes: string;
  }>
): Promise<Tariff> {
  return apiClient.patch(`admin/tariffs/${tariffId}`, { json: input }).json<Tariff>();
}

export async function deleteAdminTariff(tariffId: string): Promise<void> {
  await apiClient.delete(`admin/tariffs/${tariffId}`);
}

// ─── Payment Details ──────────────────────────────────────────────────────────

export async function getAdminPaymentDetails(): Promise<PaymentDetails> {
  return apiClient.get('admin/payment-details').json<PaymentDetails>();
}

export async function saveAdminPaymentDetails(input: {
  recipientName: string;
  inn: string;
  kpp?: string;
  checkingAccount: string;
  bankName: string;
  bic: string;
  correspondentAccount: string;
  sbpPhone?: string;
  paymentPurposePrefix?: string;
}): Promise<PaymentDetails> {
  return apiClient.post('admin/payment-details', { json: input }).json<PaymentDetails>();
}

export async function getAdminQrTest(amount?: number): Promise<{ qrString: string }> {
  const url = amount !== undefined ? `admin/payment-details/qr-test?amount=${amount}` : 'admin/payment-details/qr-test';
  return apiClient.get(url).json<{ qrString: string }>();
}

// ─── Houses (admin view) ──────────────────────────────────────────────────────

export async function getAdminHouses(): Promise<House[]> {
  return apiClient.get('houses').json<House[]>();
}

export async function createAdminHouse(input: {
  buildingNumber: string;
  fullAddress?: string;
}): Promise<House> {
  return apiClient.post('houses', { json: input }).json<House>();
}
